import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withCtx as _withCtx } from "vue";
const _hoisted_1 = {
    key: 3,
    class: "flex flex-col gap-4"
};
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Button, WidgetShell } from "@eudonet/opal";
import ModuleLoading from "@/shared/components/ModuleLoading.vue";
import ModuleState from "@/shared/components/ModuleState.vue";
import ConsentAddressCard from "@/modules/consent/presentation/components/ConsentAddressCard.vue";
import { useContactConsentsOverview } from "@/modules/consent/composables/useContactConsentsOverview";
import { isConsentRelevantChannel } from "@/modules/contact/domain/entities/contactConsent";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ContactConsentsWidget',
    props: {
        contactId: {},
        channels: {}
    },
    emits: ["view-all"],
    setup(__props) {
        const props = __props;
        const { t } = useI18n();
        // Both arguments are getters: this widget keeps one composable instance across contact
        // navigations, so a captured string would keep querying the contact it first rendered.
        const { addresses, isLoading, loadError, isForbidden, load } = useContactConsentsOverview(() => props.contactId, () => props.channels);
        /**
         * Everything a rendered card is derived from: the widget draws one card per channel it sends, and
         * each card shows the value, the Principal badge and the qualifier label (which comes from
         * `usage`, not from the consent read).
         *
         * A derived key rather than the `channels` array itself: every save on the record view replaces
         * the whole contact aggregate, so the array is a new object each time and watching its identity
         * would fire three consent reads per unrelated save.
         */
        const consentCoordinatesKey = computed(() => props.channels
            .filter(isConsentRelevantChannel)
            .map((channel) => `${channel.type}|${channel.value}|${channel.isPrimary}|${channel.usage}`)
            .join(","));
        // One entry point rather than `onMounted(load)` plus watchers: `immediate` covers the first render
        // and the sources cover every later change. (No `stop` handle is touched in the callback, so the
        // TDZ trap that bans self-stopping immediate watchers does not apply here.)
        //
        // The coordinates source is not optional (OP-7292): adding or deleting an e-mail on the record view
        // reloads the aggregate SILENTLY, which never unmounts this widget. Watching the contact id alone
        // left the card list frozen on the coordinates present at mount — a new address stayed invisible,
        // and a deleted one kept rendering its consent statuses.
        watch([() => props.contactId, consentCoordinatesKey], load, { immediate: true });
        return (_ctx, _cache) => {
            return (!_unref(isForbidden))
                ? (_openBlock(), _createBlock(_unref(WidgetShell), {
                    key: 0,
                    header: _unref(t)('contact.detail.consentsWidget.title'),
                    "data-testid": "consents-widget"
                }, {
                    actions: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            size: "sm",
                            "icon-left": "ri-eye-line",
                            label: _unref(t)('contact.detail.consentsWidget.viewAll'),
                            "data-testid": "consents-widget-view-all",
                            onClick: _cache[0] || (_cache[0] = ($event) => (_ctx.$emit('view-all')))
                        }, null, 8 /* PROPS */, ["label"])
                    ]),
                    default: _withCtx(() => [
                        (_unref(isLoading))
                            ? (_openBlock(), _createBlock(ModuleLoading, {
                                key: 0,
                                "data-testid": "consents-widget-loading"
                            }))
                            : (_unref(loadError))
                                ? (_openBlock(), _createBlock(ModuleState, {
                                    key: 1,
                                    type: "error",
                                    title: _unref(t)('contact.detail.consents.loadError'),
                                    "cta-icon": "ri-refresh-line",
                                    "cta-label": _unref(t)('contact.detail.consents.retry'),
                                    "data-testid": "consents-widget-error",
                                    onCta: _unref(load)
                                }, null, 8 /* PROPS */, ["title", "cta-label", "onCta"]))
                                : (!_unref(addresses).length)
                                    ? (_openBlock(), _createBlock(ModuleState, {
                                        key: 2,
                                        type: "empty",
                                        title: _unref(t)('contact.detail.consentsWidget.empty'),
                                        "data-testid": "consents-widget-empty"
                                    }, null, 8 /* PROPS */, ["title"]))
                                    : (_openBlock(), _createElementBlock("div", _hoisted_1, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(addresses), (address) => {
                                            return (_openBlock(), _createBlock(ConsentAddressCard, {
                                                key: `${address.addressType}|${address.value}`,
                                                address: address
                                            }, null, 8 /* PROPS */, ["address"]));
                                        }), 128 /* KEYED_FRAGMENT */))
                                    ]))
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["header"]))
                : _createCommentVNode("v-if", true);
        };
    }
});
